import { z } from 'zod'

const ApiSessionCookieResponseSchema = z.object({
  expires_at: z.iso.datetime({ offset: true }),
})

const COOKIE_FRESHNESS_MARGIN_MS = 10_000

/**
 * Tracks the host-only API session cookie that lets GETs skip the CORS preflight.
 *
 * The cookie is HttpOnly, so the page remembers which token it last stored and when
 * that token expires. A GET relies on the cookie only while the page's current Clerk
 * token is the stored one, so a sign-out or account switch never reuses a stale cookie.
 */
export function createApiSessionCookieSync({
  baseUrl,
  fetchImplementation,
}: {
  baseUrl: string
  fetchImplementation: typeof fetch
}) {
  let storedToken: string | null = null
  let storedTokenExpiresAtMs = 0
  let inFlightToken: string | null = null
  let queuedToken: string | null = null
  let isDisabled = false

  const sync = (token: string | null) => {
    if (isDisabled || token === null || token === storedToken) {
      return
    }

    if (inFlightToken === null) {
      void store(token)
    } else {
      queuedToken = token === inFlightToken ? null : token
    }
  }

  const store = async (token: string) => {
    inFlightToken = token
    try {
      const response = await fetchImplementation(
        `${baseUrl}/users/me/api-session-cookie`,
        {
          credentials: 'include',
          headers: { Authorization: `Bearer ${token}` },
          method: 'POST',
        },
      )
      const body = ApiSessionCookieResponseSchema.safeParse(
        response.ok ? await response.json() : null,
      )
      if (body.success) {
        storedToken = token
        storedTokenExpiresAtMs = Date.parse(body.data.expires_at)
      }
    } catch {
      storedToken = null
    } finally {
      const nextToken = queuedToken
      inFlightToken = null
      queuedToken = null
      sync(nextToken)
    }
  }

  return {
    canAuthenticate(token: string | null) {
      const isStoredToken = token !== null && token === storedToken
      const isUnexpired =
        storedTokenExpiresAtMs - Date.now() > COOKIE_FRESHNESS_MARGIN_MS
      return !isDisabled && isStoredToken && isUnexpired
    },
    disable() {
      isDisabled = true
    },
    sync,
  }
}
